import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchWithLocalCache } from "../lib/cache";

const columns = {
  date: "Date",
  ticker: "Ticker",
  subject: "Subject",
  sentiment: "Sentiment",
  content: "Content",
  tags: "Tags", // ✅ your API includes Tags: [{ticker, sentiment}]
};

function normalizeSentiment(s) {
  const v = String(s || "").trim().toLowerCase();
  if (v === "bullish") return "Bullish";
  if (v === "bearish") return "Bearish";
  return "Neutral";
}

function sentimentStyles(sentiment) {
  if (sentiment === "Bullish")
    return { border: "1px solid #cfe9d7", background: "#f3fbf6", color: "#1d6b3a" };
  if (sentiment === "Bearish")
    return { border: "1px solid #f2d2d2", background: "#fff5f5", color: "#8a1f1f" };
  return { border: "1px solid #e7e7e7", background: "#fafafa", color: "#555" };
}

// Split "IGV, MSFT, ORCL" etc -> ["IGV","MSFT","ORCL"]
function splitTickers(raw) {
  const s = String(raw || "").trim();
  if (!s) return [];
  return s
    .split(/[,|/;\n]+/g)
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean);
}

// Build ticker->sentiment map from Tags array
// function tagsToMap(tags) {
//   const m = new Map();
//   if (!Array.isArray(tags)) return m;
//   for (const t of tags) {
//     const тик = String(t?.ticker ?? "").trim().toUpperCase();
//     if (!тик) continue;
//     m.set(тик, normalizeSentiment(t?.sentiment));
//   }
//   return m;
// }

// --- NEW: HTML helpers (render HTML when expanded, show text preview when collapsed) ---
function stripHtml(html) {
  const s = String(html || "");
  const noTags = s.replace(/<[^>]*>/g, " ");
  return noTags
    .replace(/&nbsp;/g, " ")
    .replace(/&mdash;/g, "—")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function getPreview(html, maxLen = 180) {
  const text = stripHtml(html);
  if (!text) return "";
  return text.length > maxLen ? text.slice(0, maxLen) + "…" : text;
}

export default function Feed() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        setError("");

        const base = process.env.REACT_APP_SHEETS_API_URL;
        if (!base) throw new Error("Missing REACT_APP_SHEETS_API_URL (.env + restart)");

        const apiKey = process.env.REACT_APP_API_KEY;
        if (!apiKey) throw new Error("Missing REACT_APP_API_KEY (.env + restart)");

        const url = `${base}?type=feed&view=list&key=${encodeURIComponent(apiKey)}`;

        const res = await fetchWithLocalCache(url, "feed_list_v1", 2 * 60 * 1000);
        const payload = res?.data ?? res;
        if (payload?.error) throw new Error(payload.error);

        if (!cancelled) setItems(Array.isArray(payload) ? payload : []);
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;

    return items.filter((row) => {
      const ticker = String(row?.[columns.ticker] ?? "").toLowerCase();
      const subject = String(row?.[columns.subject] ?? "").toLowerCase();
      const content = String(row?.[columns.content] ?? "").toLowerCase();

      // search tags too (ticker/sentiment)
      const tagsArr = row?.[columns.tags];
      const tagsText = Array.isArray(tagsArr)
        ? tagsArr.map((t) => `${t?.ticker || ""} ${t?.sentiment || ""}`).join(" ").toLowerCase()
        : String(row?.[columns.sentiment] ?? "").toLowerCase();

      return ticker.includes(q) || subject.includes(q) || content.includes(q) || tagsText.includes(q);
    });
  }, [items, query]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const ad = String(a?.[columns.date] ?? "").trim();
      const bd = String(b?.[columns.date] ?? "").trim();
      if (ad < bd) return 1;
      if (ad > bd) return -1;
      return 0;
    });
  }, [filtered]);

  function toggle(idx) {
    setOpenKey((cur) => (cur === idx ? null : idx));
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: 24, fontFamily: "system-ui" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Feed</h2>

        <div style={{ marginLeft: "auto", display: "flex", gap: 12, fontSize: 14 }}>
          <Link to="/stocks" style={{ textDecoration: "none" }}>Stocks</Link>
          <Link to="/holdings" style={{ textDecoration: "none" }}>Holdings</Link>
          <Link to="/feed" style={{ textDecoration: "none" }}>Feed</Link>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search (ticker, subject, sentiment...)"
          style={{ width: 420, padding: 10 }}
        />
        <div style={{ color: "#666", fontSize: 13 }}>
          Showing {sorted.length} / {items.length}
        </div>
      </div>

      {loading && <div style={{ marginTop: 12 }}>Loading…</div>}
      {error && <div style={{ marginTop: 12, color: "crimson" }}>Error: {error}</div>}

      {!loading && !error && (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          {sorted.map((row, idx) => {
            const subject = String(row?.[columns.subject] ?? "").trim();
            const date = String(row?.[columns.date] ?? "").trim();
            const content = String(row?.[columns.content] ?? "").trim();
            const isOpen = openKey === idx;

            // ✅ Prefer Tags mapping if present
            const tagsArr = row?.[columns.tags];
           // const tagMap = tagsToMap(tagsArr);

            // fall back to Ticker list if needed
            const tickers = splitTickers(row?.[columns.ticker]);

            // Build display list: [{ticker, sentiment}]
            let pairs = [];
            if (Array.isArray(tagsArr) && tagsArr.length) {
              pairs = tagsArr
                .map((t) => ({
                  ticker: String(t?.ticker ?? "").trim().toUpperCase(),
                  sentiment: normalizeSentiment(t?.sentiment),
                }))
                .filter((p) => p.ticker);
            } else {
              // fallback: apply row sentiment to all tickers
              const rowSent = normalizeSentiment(row?.[columns.sentiment]);
              pairs = tickers.map((t) => ({ ticker: t, sentiment: rowSent }));
            }

            return (
              <div
                key={idx}
                onClick={() => toggle(idx)}
                style={{
                  border: "1px solid #eee",
                  borderRadius: 14,
                  padding: 14,
                  cursor: "pointer",
                  background: "#fff",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      {/* ✅ Ticker + sentiment pills side-by-side */}
                      {pairs.length ? (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          {pairs.map(({ ticker, sentiment }) => (
                            <span key={ticker} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                              <Link
                                to={`/stocks/${encodeURIComponent(ticker)}`}
                                onClick={(e) => e.stopPropagation()}
                                style={{ textDecoration: "none", fontWeight: 700 }}
                              >
                                {ticker}
                              </Link>
                              <span
                                style={{
                                  padding: "3px 8px",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  ...sentimentStyles(sentiment),
                                }}
                              >
                                {sentiment}
                              </span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ fontWeight: 700 }}>—</span>
                      )}

                      {date ? <span style={{ color: "#777", fontSize: 12 }}>{date}</span> : null}
                    </div>

                    <div style={{ marginTop: 6, fontSize: 15, fontWeight: 600 }}>
                      {subject || "Untitled"}
                    </div>

                    <div style={{ marginTop: 8, color: "#444", fontSize: 14, lineHeight: 1.45 }}>
                      {isOpen ? (
                        // ✅ Render HTML when expanded
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: "#222",
                            fontSize: 14,
                            lineHeight: 1.55,
                            maxWidth: "100%",
                            wordBreak: "break-word",
                          }}
                          dangerouslySetInnerHTML={{ __html: content || "<p>—</p>" }}
                        />
                      ) : (
                        // ✅ Collapsed preview (strip HTML)
                        <div style={{ color: "#666" }}>
                          {content ? getPreview(content, 180) : "—"}
                          {content ? (
                            <span style={{ marginLeft: 8, fontSize: 12, color: "#888" }}>
                              Click to expand
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ color: "#999", fontSize: 12, paddingTop: 2 }}>
                    {isOpen ? "▾" : "▸"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
